import React, { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Box } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { useTypedSelector } from "../../hooks.tsx";
import { useVoiceSession } from "../../hooks/useVoiceSession.ts";
import { voiceActions } from "../../store/voice.tsx";
import { tallyTokens } from "../../theme.tsx";
import {
  isVoiceBusy,
  publishVoiceStatus,
  registerVoiceInterrupt,
  useVoiceStatus,
} from "../../voice/voiceStatus.ts";
import VoiceControls from "./VoiceControls.tsx";
import VoiceOrb from "./VoiceOrb.tsx";
import { noticeLabel, statusLabel } from "./voiceLabels.ts";

/**
 * Owns the voice session for the whole app.
 *
 * Mounted once, high in the tree, because the session has to outlive the
 * components that start it: the microphone button lives in SearchField, and
 * asking a question from the circle view unmounts that SearchField mid-answer.
 *
 * The session normally shows up in the composer (the orb above it, Stop and
 * End in place of send), which reads it through `voiceStatus`. When no
 * composer can show it - the circle view, or a page without one - the host
 * floats the same orb and controls itself: a session must never hold the
 * microphone open with nothing on screen to end it.
 */
const VoiceModeHost: React.FC = () => {
  const dispatch = useDispatch();
  const requested = useTypedSelector((state) => state.voiceState.requested);

  const handleEnded = useCallback(() => {
    dispatch(voiceActions.exitVoiceMode());
  }, [dispatch]);

  const { context, level, interrupt } = useVoiceSession({
    active: requested,
    onEnded: handleEnded,
    copy: {
      visualFallback: i18n._(
        msg({
          message: "I have put that on screen for you.",
          context: "Spoken in voice mode when the answer is a chart or table",
        }),
      ),
      truncationSuffix: i18n._(
        msg({
          message: "The rest of the answer is on screen.",
          context: "Spoken in voice mode when a long answer is cut short",
        }),
      ),
      acknowledgements: [
        i18n._(
          msg({
            message: "Okay, let me look that up.",
            context: "Spoken in voice mode as soon as a question is heard",
          }),
        ),
        i18n._(
          msg({
            message: "One moment.",
            context: "Spoken in voice mode as soon as a question is heard",
          }),
        ),
        i18n._(
          msg({
            message: "Sure, let me check.",
            context: "Spoken in voice mode as soon as a question is heard",
          }),
        ),
      ],
      fillers: [
        i18n._(
          msg({
            message: "Still working on it.",
            context: "Spoken in voice mode when an answer is taking a while",
          }),
        ),
        i18n._(
          msg({
            message: "Bear with me.",
            context: "Spoken in voice mode when an answer is taking a while",
          }),
        ),
        i18n._(
          msg({
            message: "Running the numbers now.",
            context: "Spoken in voice mode when an answer is taking a while",
          }),
        ),
        i18n._(
          msg({
            message: "This one is taking a moment.",
            context: "Spoken in voice mode when an answer is taking a while",
          }),
        ),
      ],
    },
  });

  // Off means idle, whatever the machine last said: ending from outside does
  // not send the machine an event.
  const state = requested ? context.state : "idle";
  const notice = requested ? context.notice : null;

  useEffect(() => {
    publishVoiceStatus({ state, notice });
  }, [state, notice]);

  useEffect(() => {
    publishVoiceStatus({ level: requested ? level : 0 });
  }, [requested, level]);

  useEffect(() => {
    registerVoiceInterrupt(interrupt);
  }, [interrupt]);

  // Escape stops a response, or leaves voice mode when there is none.
  useEffect(() => {
    if (!requested) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (isVoiceBusy(state)) {
        interrupt();
      } else {
        handleEnded();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [requested, state, interrupt, handleEnded]);

  const surfaces = useVoiceStatus((status) => status.surfaces);

  if (!requested) return null;

  return (
    <>
      {/* The orb says this in motion; screen readers get it in words. */}
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        {notice ? noticeLabel(notice) : statusLabel(state)}
      </Box>
      {surfaces === 0 && (
        <Box
          data-testid="voice-fallback"
          sx={{
            position: "fixed",
            left: "50%",
            bottom: { xs: 24, sm: 32 },
            transform: "translateX(-50%)",
            // Above page content and the drawer, below dialogs.
            zIndex: 1250,
            p: "6px",
            borderRadius: 999,
            bgcolor: tallyTokens.surface,
            boxShadow: tallyTokens.shadow.md,
          }}
        >
          <VoiceOrb />
          <VoiceControls />
        </Box>
      )}
    </>
  );
};

export default VoiceModeHost;
